import { useEffect, useState, useRef } from 'react';
import { Command, Zap, Sparkles, Blocks, Plus } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import type { Prompt } from '@/types/prompt.js';

interface SlashCommandAutocompleteProps {
  isVisible: boolean;
  searchQuery: string;
  prompts: Prompt[];
  onSelectPrompt: (prompt: Prompt) => void;
  onClose: () => void;
  onCreatePrompt?: () => void;
}

export function SlashCommandAutocomplete({
  isVisible,
  searchQuery,
  prompts,
  onSelectPrompt,
  onClose,
  onCreatePrompt,
}: SlashCommandAutocompleteProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter prompts by search query
  const withoutSlash = searchQuery.startsWith('/') ? searchQuery.slice(1) : searchQuery;
  const commandName = withoutSlash.split(/\s+/)[0] || '';

  const filtered = prompts.filter(
    (prompt) =>
      prompt.name.toLowerCase().includes(commandName.toLowerCase()) ||
      (prompt.description && prompt.description.toLowerCase().includes(commandName.toLowerCase())) ||
      (prompt.title && prompt.title.toLowerCase().includes(commandName.toLowerCase()))
  );

  // Determine if "Create new prompt" should be first or last
  // If just "/" with no search, show create option first
  const showCreateFirst = commandName === '' && onCreatePrompt;
  const createPromptIndex = showCreateFirst ? 0 : filtered.length;

  // Reset selection when filtered list changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [commandName]);

  // Total items = filtered prompts + "Create new prompt" option (if onCreatePrompt exists)
  const totalItems = onCreatePrompt ? filtered.length + 1 : filtered.length;

  // Handle keyboard navigation
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        // Wrap around to first item when reaching the end
        setSelectedIndex((prev) => (prev + 1) % totalItems);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        // Wrap around to last item when reaching the beginning
        setSelectedIndex((prev) => (prev - 1 + totalItems) % totalItems);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        // Check if "Create new prompt" is selected
        if (onCreatePrompt && selectedIndex === createPromptIndex) {
          onCreatePrompt();
        } else {
          // Map selectedIndex to prompt
          const promptIndex = showCreateFirst ? selectedIndex - 1 : selectedIndex;
          if (filtered[promptIndex]) {
            onSelectPrompt(filtered[promptIndex]);
          }
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, filtered, selectedIndex, onSelectPrompt, onClose, onCreatePrompt, totalItems, createPromptIndex, showCreateFirst]);

  // Scroll selected item into view
  useEffect(() => {
    if (isVisible && containerRef.current) {
      // Calculate the actual DOM child index
      let domIndex = selectedIndex;

      if (showCreateFirst) {
        // If create is first: DOM order is [create, separator?, ...prompts]
        // selectedIndex 0 = create (DOM 0)
        // selectedIndex 1+ = prompts (DOM index needs adjustment for separator)
        if (selectedIndex === 0) {
          domIndex = 0;
        } else {
          // Skip separator if it exists (when filtered.length > 0)
          domIndex = filtered.length > 0 ? selectedIndex + 1 : selectedIndex;
        }
      } else {
        // If create is last: DOM order is [...prompts, separator?, create]
        // selectedIndex 0 to filtered.length-1 = prompts (DOM matches)
        // selectedIndex filtered.length = create (needs adjustment for separator)
        if (onCreatePrompt && selectedIndex === createPromptIndex) {
          // Create prompt is last, add offset for separator if prompts exist
          domIndex = filtered.length > 0 ? selectedIndex + 1 : selectedIndex;
        }
      }

      const selectedElement = containerRef.current.children[domIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex, isVisible, showCreateFirst, createPromptIndex, filtered.length, onCreatePrompt]);

  if (!isVisible) return null;

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'mcp':
        return <Blocks className="h-3 w-3" />;
      case 'config':
        return <Command className="h-3 w-3" />;
      case 'custom':
        return <Sparkles className="h-3 w-3" />;
      default:
        return <Zap className="h-3 w-3" />;
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'mcp':
        return 'text-blue-600 bg-blue-50';
      case 'config':
        return 'text-purple-600 bg-purple-50';
      case 'custom':
        return 'text-amber-600 bg-amber-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-popover border rounded-lg shadow-lg max-h-64 overflow-y-auto z-[100]">
      <div className="p-2 border-b bg-muted/30">
        <p className="text-xs text-muted-foreground flex items-center gap-2">
          <Command className="h-3 w-3" />
          {filtered.length > 0
            ? `${filtered.length} prompt${filtered.length > 1 ? 's' : ''} available`
            : 'No prompts found'}
        </p>
      </div>

      <div ref={containerRef} className="py-1">
        {/* Create new prompt option - shown first if just "/" */}
        {showCreateFirst && (
          <>
            <div
              onClick={onCreatePrompt}
              className={cn(
                'px-3 py-2.5 cursor-pointer transition-colors',
                selectedIndex === 0 ? 'bg-primary/10' : 'hover:bg-muted'
              )}
            >
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">
                  Create new prompt
                </span>
                {selectedIndex === 0 && (
                  <span className="ml-auto text-[10px] text-primary font-medium">
                    Press Enter
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 ml-6">
                Define a custom slash command
              </p>
            </div>
            {filtered.length > 0 && <div className="border-t my-1" />}
          </>
        )}

        {/* Prompts list */}
        {filtered.map((prompt, promptIndex) => {
          const displayIndex = showCreateFirst ? promptIndex + 1 : promptIndex;
          return (
            <div
              key={prompt.name}
              onClick={() => onSelectPrompt(prompt)}
              className={cn(
                'px-3 py-2 cursor-pointer transition-colors',
                displayIndex === selectedIndex ? 'bg-muted' : 'hover:bg-muted'
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-sm font-medium text-foreground">
                      /{prompt.name}
                    </span>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium',
                        getSourceColor(prompt.source)
                      )}
                    >
                      {getSourceIcon(prompt.source)}
                      {prompt.source.toUpperCase()}
                    </span>
                  </div>
                  {(prompt.title || prompt.description) && (
                    <p className="text-xs text-muted-foreground truncate">
                      {prompt.title || prompt.description}
                    </p>
                  )}
                  {prompt.arguments && prompt.arguments.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {prompt.arguments.map((arg) => (
                        <span
                          key={arg.name}
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground font-mono"
                        >
                          {arg.name}
                          {arg.required && <span className="text-destructive ml-0.5">*</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Create new prompt option - shown last if searching */}
        {!showCreateFirst && onCreatePrompt && (
          <>
            {filtered.length > 0 && <div className="border-t my-1" />}
            <div
              onClick={onCreatePrompt}
              className={cn(
                'px-3 py-2.5 cursor-pointer transition-colors',
                selectedIndex === createPromptIndex ? 'bg-primary/10' : 'hover:bg-muted'
              )}
            >
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">
                  Create new prompt
                </span>
                {selectedIndex === createPromptIndex && (
                  <span className="ml-auto text-[10px] text-primary font-medium">
                    Press Enter
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 ml-6">
                Define a custom slash command
              </p>
            </div>
          </>
        )}

        {/* Empty state - only show if no prompts AND no create option */}
        {filtered.length === 0 && !onCreatePrompt && (
          <div className="px-3 py-4 text-center">
            <p className="text-sm text-muted-foreground">No prompts match your search</p>
          </div>
        )}
      </div>

      <div className="p-2 border-t bg-muted/30">
        <p className="text-[10px] text-muted-foreground">
          <span className="font-medium">↑↓</span> Navigate • <span className="font-medium">Tab/Enter</span> Select • <span className="font-medium">Esc</span> Close
        </p>
      </div>
    </div>
  );
}
